import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, extractError, API_BASE, TOKEN_KEY } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { formatMoney, formatDateTime } from "../../lib/money";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription } from "../../components/ui/alert";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { ChevronLeft, Pencil, Trash2, MessageSquare, Receipt as ReceiptIcon, Truck, Container, User as UserIcon, AlertTriangle, FileText, ExternalLink } from "lucide-react";
import { toast } from "sonner";

function TmsIcon({ type, className = "h-4 w-4" }) {
  if (type === "truck") return <Truck className={className} />;
  if (type === "trailer") return <Container className={className} />;
  if (type === "driver") return <UserIcon className={className} />;
  return null;
}

function StatusBadge({ status }) {
  const cls =
    status === "pending"
      ? "bg-amber-50 text-amber-700"
      : status === "matched"
      ? "bg-emerald-50 text-emerald-700"
      : "bg-slate-100 text-slate-600";
  return <Badge variant="secondary" className={cls}>{status}</Badge>;
}

export default function ExpenseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPrivileged = user?.role === "admin" || user?.role === "accounting";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expense, setExpense] = useState(null);
  const [card, setCard] = useState(null);
  const [category, setCategory] = useState(null);
  const [department, setDepartment] = useState(null);
  const [ownerEmail, setOwnerEmail] = useState(null);
  const [receiptUrl, setReceiptUrl] = useState(null);
  const [receiptContentType, setReceiptContentType] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get(`/expenses/${id}`);
      setExpense(data);

      const reqs = [];
      reqs.push(api.get("/cards/available").catch(() => ({ data: [] })));
      reqs.push(api.get("/categories/active").catch(() => ({ data: [] })));
      reqs.push(api.get("/departments/active").catch(() => ({ data: [] })));
      if (isPrivileged) reqs.push(api.get("/users/workers").catch(() => ({ data: [] })));
      const [c, cat, p, u] = await Promise.all(reqs);
      setCard(c.data.find((x) => x.id === data.card_id) || null);
      setCategory(cat.data.find((x) => x.id === data.category_id) || null);
      setDepartment(data.department_id ? p.data.find((x) => x.id === data.department_id) || null : null);
      if (u) setOwnerEmail(u.data.find((x) => x.id === data.user_id)?.email || null);

      if (data.receipt_image_id) {
        const token = localStorage.getItem(TOKEN_KEY);
        const res = await fetch(`${API_BASE}/receipts/${data.receipt_image_id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const blob = await res.blob();
          setReceiptUrl(URL.createObjectURL(blob));
          // Prefer the expense doc's stored content-type (accurate for legacy
          // uploads where the browser may report a generic mime); fall back to
          // the blob's own type. If both are missing, downstream code treats
          // it as an image (legacy behavior).
          setReceiptContentType(data.receipt_content_type || blob.type || null);
        }
      }
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }, [id, isPrivileged]);

  useEffect(() => { load(); return () => { if (receiptUrl) URL.revokeObjectURL(receiptUrl); }; }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  const canEdit = expense && (isPrivileged || (expense.user_id === user?.id && expense.status === "pending"));
  const canDelete = canEdit;

  const onDelete = async () => {
    try {
      await api.delete(`/expenses/${id}`);
      toast.success("Expense deleted");
      navigate(user?.role === "worker" ? "/" : "/expenses");
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setConfirmDel(false);
    }
  };

  if (loading) return <p className="py-10 text-center text-slate-500" data-testid="detail-loading">Loading…</p>;
  if (error) return <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>;
  if (!expense) return null;

  return (
    <div data-testid="expense-detail-page" className="max-w-3xl">
      <button
        onClick={() => navigate(-1)}
        className="mb-4 inline-flex items-center text-sm text-slate-500 hover:text-slate-900"
        data-testid="detail-back-btn"
      >
        <ChevronLeft className="mr-1 h-4 w-4" /> Back
      </button>

      <Card className="border-slate-200">
        <CardHeader className="space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-2xl font-semibold text-slate-900">{expense.vendor}</CardTitle>
              <p className="mt-1 text-sm text-slate-500">{formatDateTime(expense.occurred_at)}</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-slate-900">{formatMoney(expense.amount_cents, expense.currency)}</p>
              <div className="mt-1 flex justify-end"><StatusBadge status={expense.status} /></div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 pt-4">
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Card</dt>
              <dd className="mt-1 text-slate-900" data-testid="detail-card">
                {expense.card_label
                  ? `${expense.card_label} (•••• ${expense.card_last4 || "????"})`
                  : card ? `${card.label} (•••• ${card.last4})` : expense.card_id}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Category</dt>
              <dd className="mt-1 text-slate-900" data-testid="detail-category">{expense.category_name || category?.name || "—"}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Company</dt>
              <dd className="mt-1 text-slate-900" data-testid="detail-company">
                {expense.company_code
                  ? `${expense.company_code} — ${expense.company_name || ""}`
                  : "—"}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Department</dt>
              <dd className="mt-1 text-slate-900" data-testid="detail-department">
                {expense.department_code
                  ? `${expense.department_code} — ${expense.department_name || ""}`
                  : department ? `${department.code} — ${department.name}` : "—"}
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-xs uppercase tracking-wide text-slate-500">TMS links</dt>
              <dd className="mt-1 grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="detail-tms">
                {[
                  { key: "trucks", label: "Trucks", type: "truck", items: expense.trucks || [] },
                  { key: "trailers", label: "Trailers", type: "trailer", items: expense.trailers || [] },
                  { key: "drivers", label: "Drivers", type: "driver", items: expense.drivers || [] },
                ].map((g) => (
                  <div key={g.key} data-testid={`detail-tms-${g.key}`}>
                    <p className="text-xs font-medium text-slate-600">{g.label}</p>
                    {g.items.length === 0 ? (
                      <p className="mt-1 text-sm text-slate-400">None</p>
                    ) : (
                      <ul className="mt-1 space-y-1">
                        {g.items.map((it) => (
                          <li key={it.id} className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-[#0F1E3D]/5 px-2 py-1 text-xs text-[#0F1E3D] ring-1 ring-[#0F1E3D]/15"
                            data-testid={`detail-tms-${g.key}-${it.id}`}>
                            <TmsIcon type={g.type} className="h-3 w-3" />
                            <span className="truncate">{it.label || it.id}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Charge driver</dt>
              <dd className="mt-1" data-testid="detail-charge-driver">
                {expense.charge_driver ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                    <AlertTriangle className="h-3 w-3" /> Charged back to driver
                  </span>
                ) : <span className="text-sm text-slate-400">No</span>}
              </dd>
            </div>
            {isPrivileged && (
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-500">Worker</dt>
                <dd className="mt-1 text-slate-900" data-testid="detail-owner">
                  {expense.user_name
                    ? `${expense.user_name}${expense.user_email ? ` (${expense.user_email})` : ""}`
                    : ownerEmail || expense.user_id}
                </dd>
              </div>
            )}
            <div className="sm:col-span-2">
              <dt className="text-xs uppercase tracking-wide text-slate-500">Comment</dt>
              <dd className="mt-1 whitespace-pre-wrap text-slate-900" data-testid="detail-comment">
                {expense.comment || <span className="text-slate-400">No comment</span>}
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-xs uppercase tracking-wide text-slate-500">Receipt</dt>
              <dd className="mt-1">
                {(() => {
                  if (!receiptUrl && !expense.receipt_image_id) {
                    return <p className="text-sm text-slate-400">No receipt</p>;
                  }
                  if (!receiptUrl) {
                    return <p className="text-sm text-slate-500">Receipt attached (couldn&apos;t load preview).</p>;
                  }
                  // Toll attribution — matches the row hover-card format.
                  const src = expense.receipt_source;
                  const tollDocId = src?.kind === "toll_app"
                    ? (src.document_id || expense.receipt_file_id || expense.receipt_image_id || "")
                    : null;
                  const tollTag = tollDocId ? (
                    <p className="mt-2 text-xs italic text-slate-500" data-testid="detail-receipt-toll-source">
                      From Toll · document {String(tollDocId).slice(-8)}
                    </p>
                  ) : null;
                  const isPdf = (receiptContentType || "").startsWith("application/pdf");
                  if (isPdf) {
                    const fname = expense.receipt_filename || "receipt.pdf";
                    const sizeBytes = expense.receipt_size_bytes;
                    const sizeLabel = sizeBytes
                      ? sizeBytes >= 1024 * 1024
                        ? `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`
                        : `${(sizeBytes / 1024).toFixed(1)} KB`
                      : "";
                    return (
                      <div className="max-w-sm">
                        <a
                          href={receiptUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 rounded-md border border-slate-200 bg-slate-50 p-3 hover:border-slate-300 hover:bg-slate-100"
                          data-testid="detail-receipt-pdf"
                        >
                          <FileText className="h-10 w-10 shrink-0 text-rose-600" />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-slate-900" title={fname}>{fname}</p>
                            <p className="text-xs text-slate-500">PDF{sizeLabel ? ` · ${sizeLabel}` : ""}</p>
                          </div>
                          <ExternalLink className="h-4 w-4 shrink-0 text-slate-400" />
                        </a>
                        {tollTag}
                      </div>
                    );
                  }
                  return (
                    <div className="max-w-sm">
                      <button
                        type="button"
                        onClick={() => setShowReceipt(true)}
                        className="inline-block rounded border border-slate-200 p-1 hover:border-slate-300"
                        data-testid="detail-receipt-thumb"
                      >
                        <img src={receiptUrl} alt="Receipt" className="h-28 w-28 rounded object-cover" />
                      </button>
                      {tollTag}
                    </div>
                  );
                })()}
              </dd>
            </div>
          </dl>
          <p className="text-xs text-slate-400">
            Logged {formatDateTime(expense.created_at)}
            {expense.updated_at ? ` · updated ${formatDateTime(expense.updated_at)}` : ""}
          </p>
        </CardContent>
      </Card>

      {(canEdit || canDelete) && (
        <div className="mt-6 flex flex-wrap gap-2">
          {canEdit && (
            <Link to={`/expenses/${expense.id}/edit`} data-testid="detail-edit-link">
              <Button variant="outline"><Pencil className="mr-1 h-4 w-4" /> Edit</Button>
            </Link>
          )}
          {canDelete && (
            <Button variant="outline" onClick={() => setConfirmDel(true)} data-testid="detail-delete-btn"
              className="border-rose-200 text-rose-600 hover:bg-rose-50">
              <Trash2 className="mr-1 h-4 w-4" /> Delete
            </Button>
          )}
        </div>
      )}

      <Dialog open={confirmDel} onOpenChange={setConfirmDel}>
        <DialogContent data-testid="confirm-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete this expense?</DialogTitle>
            <DialogDescription>
              {expense.vendor} · {formatMoney(expense.amount_cents, expense.currency)}. This action soft-deletes the entry.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDel(false)}>Cancel</Button>
            <Button onClick={onDelete} className="bg-rose-600 text-white hover:bg-rose-700" data-testid="confirm-delete-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showReceipt} onOpenChange={setShowReceipt}>
        <DialogContent className="max-w-2xl" data-testid="receipt-modal">
          <DialogHeader>
            <DialogTitle>Receipt</DialogTitle>
          </DialogHeader>
          {receiptUrl && (
            (receiptContentType || "").startsWith("application/pdf") ? (
              <iframe
                title="Receipt"
                src={receiptUrl}
                className="h-[70vh] w-full rounded bg-white"
                data-testid="receipt-modal-pdf"
              />
            ) : (
              <img
                src={receiptUrl}
                alt="Receipt"
                data-testid="receipt-modal-image"
                className="max-h-[70vh] w-full rounded object-contain"
              />
            )
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
